import { Drawer, DrawerClose, DrawerContent } from "@/components/ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { FC, useState } from "react";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useUpdateVirtualPosDetailsMutation } from "@/lib/services/data-layer/core";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { formatPersianDate } from "@/lib/helper";
import InputWithLabel from "@/components/v-pos/inputs/input-with-label.component";
import TextareaWithLabel from "@/components/v-pos/inputs/textarea-with-label.component";
import { TfiMoreAlt } from "react-icons/tfi";

type editDataDrawerProps = {
  description?: string;
  fullName?: string;
  updatedAt: string;
  reportId: number;
};

const EditDataDrawer: FC<editDataDrawerProps> = ({
  fullName,
  description,
  reportId,
  updatedAt,
}) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const form = useForm({
    defaultValues: {
      fullName: fullName ?? "",
      description: description ?? "",
    },
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const { mutate, isPending } = useUpdateVirtualPosDetailsMutation();

  const onSubmit = (values: { fullName: string; description: string }) => {
    mutate(
      { id: String(reportId), ...values },
      {
        onError(e) {
          toast.error(e.message);
        },
        onSuccess() {
          toast.success("باموفقیت ویرایش شد");
          queryClient.invalidateQueries({
            queryKey: ["virtual-pos-list"],
          });
          setIsOpen(false);
        },
      },
    );
  };

  const closeDrawer = () => {
    setIsOpen(false);
    form.reset();
  };

  const openDrawer = () => {
    setIsOpen(true);
  };

  return (
    <Drawer open={isOpen} onOpenChange={closeDrawer}>
      <Button
        className="cursor-pointer flex-none p-1  h-[24px] w-[24px]"
        variant="link"
        onClick={openDrawer}
      >
        <TfiMoreAlt  className="text-gray-500 text-4xl" />
      </Button>
      <DrawerContent className="px-3">
        <DialogTitle />
        <DialogDescription />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full pt-4 max-w-[500px] mx-auto space-y-[18px]"
          >
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputWithLabel
                      label="نام و نام خانوادگی مشتری"
                      {...field}
                      className="rounded-2xl"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <TextareaWithLabel
                      className="w-full field-sizing-fixed rounded-2xl"
                      rows={3}
                      label="توضیحات"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-start text-[10px] pr-1" />
                </FormItem>
              )}
              
            />
            {!isNaN(new Date(updatedAt).getTime()) && (
              <p>
                آخرین ویرایش:{" "}
                {formatPersianDate({ dateString: updatedAt, mode: "all" })}
              </p>
            )}

            <div className="flex flex-row justify-center gap-[18px] mb-[20px] mt-[20px] px-2">
              <DrawerClose asChild>
                <Button
                  type="button"
                  className="flex-auto font-bold rounded-2xl"
                  disabled={isPending}
                  variant="secondary"
                  onClick={() => form.reset()}
                >
                  انصراف
                </Button>
              </DrawerClose>
              <Button
                type="submit"
                className="flex basis-1/3 font-bold rounded-2xl "
                disabled={!form.formState.isValid || isPending}
              >
                {isPending ? "درحال ارسال..." : "به روز رسانی"}
              </Button>
            </div>
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default EditDataDrawer;
